import { failure, success, type Result } from './types.ts';
export type LetterMappingType = 'SDB' | 'EDB' | 'PAA' | 'TAA' | 'SPOT' | 'SBIR' | 'CRAD';
export type LetterVariant = 'any' | 'cash_only' | 'options_only' | 'cash_and_options';
export type LetterSignerSource = 'executive' | 'sector_lead' | 'ceo' | 'person';
export interface LetterPolicy {
  readonly awardType: LetterMappingType;
  readonly source: LetterSignerSource;
  readonly personId: string | null;
}
export const defaultLetterPolicies: readonly LetterPolicy[] = Object.freeze(
  (
    [
      { awardType: 'SDB', source: 'sector_lead', personId: null },
      { awardType: 'EDB', source: 'executive', personId: null },
      ...(['PAA', 'TAA', 'SPOT', 'SBIR', 'CRAD'] as const).map((awardType) => ({
        awardType,
        source: 'ceo' as const,
        personId: null,
      })),
    ] satisfies LetterPolicy[]
  ).map((p) => Object.freeze(p)),
);
export interface MappedLetterTemplate {
  readonly id: string;
  readonly awardType: string;
  readonly signerId: string;
  readonly variant: string;
  readonly approved: boolean;
  readonly usable: boolean;
  readonly mapped: boolean;
}
export interface LetterMappingInput {
  readonly awardType: LetterMappingType;
  readonly variant: LetterVariant;
  readonly giverId: string | null;
  readonly sectorLeadIds: readonly string[];
  readonly ceoId: string | null;
  readonly policies: readonly LetterPolicy[];
  readonly templates: readonly MappedLetterTemplate[];
  readonly overrideId?: string;
  readonly overrideReason?: string;
}
export function resolveLetterMapping(
  input: LetterMappingInput,
): Result<{ templateId: string; signerId: string; source: LetterSignerSource | 'override' }> {
  const compatible = (t: MappedLetterTemplate) =>
    t.awardType === input.awardType &&
    t.approved &&
    t.usable &&
    (t.variant === input.variant || t.variant === 'any');
  if (input.overrideId !== undefined) {
    if (!input.overrideReason?.trim()) return failure('letter_override_reason_required');
    const t = input.templates.find((t) => t.id === input.overrideId && compatible(t));
    return t
      ? success({ templateId: t.id, signerId: t.signerId, source: 'override' })
      : failure('letter_override_incompatible');
  }
  const signer = resolveLetterSigner(input);
  if (!signer.ok) return signer;
  const { signerId, source } = signer.value;
  const matches = input.templates.filter(
    (t) => compatible(t) && t.mapped && t.signerId === signerId,
  );
  // Exact variant takes precedence over an explicitly mapped general-purpose design.
  const exact = matches.filter((t) => t.variant === input.variant);
  const candidates = exact.length ? exact : matches;
  if (candidates.length !== 1)
    return failure(candidates.length ? 'letter_mapping_ambiguous' : 'letter_mapping_missing');
  const candidate = candidates[0];
  if (!candidate) return failure('letter_mapping_missing');
  return success({ templateId: candidate.id, signerId, source });
}

/**
 * APP-212: what a person's letter is made from once the built-in sample letter is the safety net.
 * `template`: the real letter `resolveLetterMapping` resolves (or a one-off override). Otherwise
 * `built_in_sample`: MyRewards' own sample letter for the award type, which has no signer of its
 * own, so the signer the policy names merges into it.
 */
export type LetterResolution =
  | {
      readonly kind: 'template';
      readonly templateId: string;
      readonly signerId: string;
      readonly source: LetterSignerSource | 'override';
    }
  | {
      readonly kind: 'built_in_sample';
      readonly signerId: string;
      readonly source: LetterSignerSource;
    };

/**
 * APP-212 (QAD-005 §7, Tom: "initial default system install is the sample letter marked sample"):
 * `resolveLetterMapping`, with the built-in sample standing in only where no real letter fits: the
 * policy names one signer and none of their letters fits the mix. Never over a problem (no policy,
 * no signer or two, two letters in one cell) and never over a one-off override, which stays
 * explicit. `builtInSample` says whether the award type has a built-in sample at all.
 */
export function resolveLetterOrBuiltInSample(
  input: LetterMappingInput & { readonly builtInSample: boolean },
): Result<LetterResolution> {
  const mapped = resolveLetterMapping(input);
  if (mapped.ok) return success({ kind: 'template', ...mapped.value });
  if (!input.builtInSample || input.overrideId !== undefined) return mapped;
  if (!mapped.issues.every((i) => i.code === 'letter_mapping_missing')) return mapped;
  // A missing letter means the signer resolved: the template is all that failed.
  const signer = resolveLetterSigner(input);
  if (!signer.ok) return signer;
  return success({ kind: 'built_in_sample', ...signer.value });
}

/**
 * Who signs an award's letters under its type's signing policy, before any letter is matched: the
 * first step of `resolveLetterMapping`, on its own so a letter with no template yet can still name
 * its cell (the gap card, `letterCellKey`) and the entry step can say who signs (APP-130). One
 * rule, so they never disagree.
 */
export function resolveLetterSigner(
  input: Pick<LetterMappingInput, 'awardType' | 'giverId' | 'sectorLeadIds' | 'ceoId' | 'policies'>,
): Result<{ signerId: string; source: LetterSignerSource }> {
  const policies = input.policies.filter((p) => p.awardType === input.awardType);
  if (policies.length !== 1) return failure('letter_policy_missing');
  const policy = policies[0];
  if (!policy) return failure('letter_policy_missing');
  const ids =
    policy.source === 'sector_lead'
      ? [...new Set(input.sectorLeadIds)]
      : [
          policy.source === 'executive'
            ? input.giverId
            : policy.source === 'ceo'
              ? input.ceoId
              : policy.personId,
        ].filter((id): id is string => id !== null);
  if (ids.length !== 1)
    return failure(ids.length ? 'letter_signer_ambiguous' : 'letter_signer_missing');
  const signerId = ids[0];
  if (!signerId) return failure('letter_signer_missing');
  return success({ signerId, source: policy.source });
}
